

import React from "react";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, PiggyBank, Users, Gift } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface StepProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  example?: string;
  onClick?: () => void;
}

const Step = ({ number, icon, title, description, example, onClick }: StepProps) => {
  return (
    <div className="group relative cursor-pointer h-full" onClick={onClick}>
      <div className="relative z-10 p-4 md:p-6 flex flex-col items-center text-center h-full bg-white rounded-xl md:rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
        <div className="relative mb-4 flex-shrink-0">
          <div className="bg-gradient-to-br from-legatia-yellow/20 to-legatia-orange/20 rounded-lg md:rounded-xl w-12 h-12 md:w-16 md:h-16 flex items-center justify-center border border-legatia-orange/20">
            {icon}
          </div>
          
          <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-gradient-to-r from-legatia-orange to-legatia-red text-white w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm shadow-lg">
            {number}
          </div>
        </div>
        
        <div className="flex-1 flex flex-col justify-center space-y-2 md:space-y-3">
          <h3 className="text-sm md:text-lg lg:text-xl font-bold text-gray-900 leading-tight min-h-[2.5rem] md:min-h-[3rem] flex items-center justify-center">
            {title}
          </h3>
          <p className="text-gray-600 text-xs md:text-sm leading-relaxed flex-1 flex items-center">
            {description}
          </p>
          {example && (
            <p className="text-legatia-orange text-xs md:text-sm font-medium italic mt-2">
              {example}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ProcessFlow = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Comment créer leur cagnotte cadeau ?",
      steps: [
        {
          title: "Choisissez le moment de vie",
          description: "Sélectionnez le moment de vie auquel vous souhaitez participer.",
          example: "Ex: Pour ses 18 ans"
        },
        {
          title: "Ajoutez votre message",
          description: "Écrivez-leur un mot du cœur qu'ils découvriront ce jour-là.",
          example: "Ex: Une vidéo enregistrée"
        },
        {
          title: "Investissez pour leur avenir",
          description: "Vos contributions sont investies et génèrent des rendements au fil du temps.",
          example: "Ex: 50€ par mois"
        },
        {
          title: "Invitez la famille",
          description: "Grands-parents, oncles et tantes peuvent aussi participer.",
          example: "Ex: Toute la famille contribue"
        },
        {
          title: "Laissez la magie opérer",
          description: "Le jour J, ils reçoivent vos messages et une belle surprise financière.",
          example: "Ex: 15 000€ + vos mots d'amour"
        }
      ],
      buttonText: "Créer leur cagnotte cadeau"
    },
    en: {
      title: "How to create their gift fund?",
      steps: [
        {
          title: "Choose the life moment",
          description: "Select an important moment where you want to be present.",
          example: "e.g., For their 18th birthday"
        },
        {
          title: "Add your message",
          description: "Write them a heartfelt note they'll discover on that day.",
          example: "e.g., A recorded video"
        },
        {
          title: "Invest for their future",
          description: "Your contributions are invested and generate returns over time.",
          example: "e.g., €50 per month"
        },
        {
          title: "Invite the family",
          description: "Grandparents, uncles and aunts can also participate.",
          example: "e.g., The whole family contributes"
        },
        {
          title: "Let the magic happen",
          description: "On the big day, they receive your messages and a beautiful financial surprise.",
          example: "e.g., €15,000 + your words of love"
        }
      ],
      buttonText: "Create their gift fund"
    },
    de: {
      title: "Wie erstelle ich ihren Geschenkfonds?",
      steps: [
        {
          title: "Wählen Sie den Lebensmoment",
          description: "Wählen Sie einen wichtigen Moment, in dem Sie präsent sein möchten.",
          example: "z.B., Für ihren 18. Geburtstag"
        },
        {
          title: "Fügen Sie Ihre Nachricht hinzu",
          description: "Schreiben Sie ihnen eine herzliche Notiz, die sie an diesem Tag entdecken.",
          example: "z.B., Ein aufgenommenes Video"
        },
        {
          title: "Investieren Sie in ihre Zukunft",
          description: "Ihre Beiträge werden investiert und generieren Renditen über die Zeit.",
          example: "z.B., 50€ pro Monat"
        },
        {
          title: "Laden Sie die Familie ein",
          description: "Großeltern, Onkel und Tanten können auch teilnehmen.",
          example: "z.B., Die ganze Familie trägt bei"
        },
        {
          title: "Lassen Sie die Magie geschehen",
          description: "Am großen Tag erhalten sie Ihre Nachrichten und eine schöne finanzielle Überraschung.",
          example: "z.B., 15.000€ + Ihre Liebesworte"
        }
      ],
      buttonText: "Geschenkfonds erstellen"
    }
  };
  
  const icons = [
    <PiggyBank key="piggybank" className="h-5 w-5 md:h-6 md:w-6 text-legatia-orange" />,
    <MessageCircle key="message" className="h-5 w-5 md:h-6 md:w-6 text-legatia-orange" />,
    <Heart key="heart" className="h-5 w-5 md:h-6 md:w-6 text-legatia-orange" />,
    <Users key="users" className="h-5 w-5 md:h-6 md:w-6 text-legatia-orange" />,
    <Gift key="gift" className="h-5 w-5 md:h-6 md:w-6 text-legatia-orange" />
  ];
  
  return (
    <section id="how-it-works" className="py-12 md:py-16 lg:py-20 px-4 bg-gradient-to-b from-white to-legatia-green/5">
      <div className="container mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            {content[language].title}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6 mb-8 md:mb-12">
          {content[language].steps.map((step, index) => (
            <div key={index} className="h-full">
              <Step 
                number={index + 1} 
                icon={icons[index]} 
                title={step.title} 
                description={step.description}
                example={step.example}
                onClick={() => window.location.href = "https://app.getlegatia.ch/"}
              />
            </div>
          ))}
        </div>
        
        <div className="text-center px-4">
          <Button 
            className="bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white text-base md:text-lg px-6 md:px-8 py-3 md:py-4 rounded-full transition-all transform hover:shadow-lg hover:-translate-y-1 flex items-center gap-2 w-full sm:w-auto sm:mx-auto max-w-sm sm:max-w-none"
            onClick={() => window.location.href = "https://app.getlegatia.ch/"}
          >
            <Heart className="h-4 w-4 md:h-5 md:w-5" />
            {content[language].buttonText}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProcessFlow;

